import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wind, Droplets, Thermometer, Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

interface AQIPanelProps {
  unitId: string;
}

const getAQIStatus = (value: number): { status: string; color: string; bg: string } => {
  if (value <= 50) return { status: 'Good', color: 'text-success', bg: 'bg-success/20' };
  if (value <= 100) return { status: 'Moderate', color: 'text-warning', bg: 'bg-warning/20' };
  if (value <= 150) return { status: 'Unhealthy (Sensitive)', color: 'text-orange-500', bg: 'bg-orange-500/20' };
  if (value <= 200) return { status: 'Unhealthy', color: 'text-error', bg: 'bg-error/20' };
  return { status: 'Very Unhealthy', color: 'text-red-700', bg: 'bg-red-700/20' };
};

const getCO2Status = (value: number): { status: string; color: string } => {
  if (value < 600) return { status: 'Excellent', color: 'text-success' };
  if (value < 800) return { status: 'Good', color: 'text-success' };
  if (value < 1000) return { status: 'Moderate', color: 'text-warning' };
  if (value < 1500) return { status: 'Poor', color: 'text-orange-500' };
  return { status: 'Unhealthy', color: 'text-error' };
};

/** What a real sensor reading will look like once the hardware is installed. */
interface AQIReading {
  co2: number;
  pm25: number;
  voc: number;
  temperature: number;
  humidity: number;
  overallAQI: number;
  history: Array<{ timestamp: string; co2: number; pm25: number; aqi: number }>;
}

const NO_READING = '\u2014';

/**
 * The seam where a real sensor feed will attach.
 *
 * Air-quality hardware is not installed on the fleet yet, so this returns null
 * and the panel renders "no reading" throughout. It previously came from
 * generateMockAQIData(), which invented CO2, PM2.5, VOC, temperature, humidity,
 * an overall AQI and 24 hours of history from Math.random() on every render —
 * in production, with no demo gate, styled exactly like measured values. An
 * empty panel is honest; a green "Good" reading from a sensor that does not
 * exist is not.
 */
function readAirQuality(_unitId: string): AQIReading | null {
  return null;
}

export function AQIPanel({ unitId: _unitId }: AQIPanelProps) {
  const reading = readAirQuality(_unitId);

  const aqiStatusInfo = reading
    ? getAQIStatus(reading.overallAQI)
    : { status: 'No sensor', color: 'text-muted-foreground', bg: 'bg-muted/30' };
  const co2StatusInfo = reading
    ? getCO2Status(reading.co2)
    : { status: 'No sensor', color: 'text-muted-foreground' };

  // Chart data
  const timeLabels = (reading?.history ?? []).map(h =>
    new Date(h.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
  const aqiData = (reading?.history ?? []).map(h => h.aqi);
  const co2Data = (reading?.history ?? []).map(h => h.co2);

  // AQI trend chart options
  const aqiChartOptions: ApexOptions = {
    chart: {
      type: 'area',
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#8FB560'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val) => `${Math.round(val)}`,
      },
      title: { text: 'AQI', style: { color: '#9CA3AF', fontSize: '10px' } },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val) => `AQI: ${Math.round(val)}` },
    },
    dataLabels: { enabled: false },
  };

  // CO2 trend chart options
  const co2ChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#3B82F6'],
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val) => `${Math.round(val)}`,
      },
      title: { text: 'ppm', style: { color: '#9CA3AF', fontSize: '10px' } },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val) => `${Math.round(val)} ppm` },
    },
    dataLabels: { enabled: false },
  };

  return (
    <Card className="lg:min-h-[647px] flex flex-col mb-4 lg:mb-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-success" />
            <CardTitle>Air Quality Index</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            Mock Data
          </Badge>
        </div>
        <CardDescription>Indoor air quality monitoring</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 flex-1">
        {/* Overall AQI */}
        <div className={`${aqiStatusInfo.bg} rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Overall Air Quality Index</p>
              <div className="flex items-center gap-2">
                <span className={`text-4xl font-bold ${aqiStatusInfo.color}`}>
                  {reading ? reading.overallAQI : NO_READING}
                </span>
                <div className="flex items-center gap-1">
                  {!reading ? null : reading.overallAQI <= 50 ? (
                    <CheckCircle2 className={`h-5 w-5 ${aqiStatusInfo.color}`} />
                  ) : (
                    <AlertTriangle className={`h-5 w-5 ${aqiStatusInfo.color}`} />
                  )}
                  <span className={`text-sm font-medium ${aqiStatusInfo.color}`}>
                    {aqiStatusInfo.status}
                  </span>
                </div>
              </div>
            </div>
            <Activity className={`h-12 w-12 ${aqiStatusInfo.color} opacity-50`} />
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* CO2 */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Wind className="h-4 w-4" />
              <span>CO₂</span>
            </div>
            <div className={`text-2xl font-bold ${co2StatusInfo.color}`}>
              {reading ? reading.co2 : NO_READING}
              {reading && <span className="text-sm text-muted-foreground ml-1">ppm</span>}
            </div>
            <div className={`text-xs ${co2StatusInfo.color}`}>{co2StatusInfo.status}</div>
          </div>

          {/* PM2.5 */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Droplets className="h-4 w-4" />
              <span>PM2.5</span>
            </div>
            <div className="text-2xl font-bold">
              {reading ? formatNumber(reading.pm25, 1) : NO_READING}
              {reading && <span className="text-sm text-muted-foreground ml-1">\u00b5g/m\u00b3</span>}
            </div>
            <div className="text-xs text-muted-foreground">{reading ? 'Good' : 'No sensor'}</div>
          </div>

          {/* VOC */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Activity className="h-4 w-4" />
              <span>VOC</span>
            </div>
            <div className="text-2xl font-bold">
              {reading ? reading.voc : NO_READING}
              {reading && <span className="text-sm text-muted-foreground ml-1">ppb</span>}
            </div>
            <div className="text-xs text-muted-foreground">{reading ? 'Good' : 'No sensor'}</div>
          </div>

          {/* Temperature */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Thermometer className="h-4 w-4" />
              <span>Temperature</span>
            </div>
            <div className="text-2xl font-bold">
              {reading ? reading.temperature : NO_READING}
              {reading && <span className="text-sm text-muted-foreground ml-1">\u00b0F</span>}
            </div>
          </div>

          {/* Humidity */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Droplets className="h-4 w-4" />
              <span>Humidity</span>
            </div>
            <div className="text-2xl font-bold">
              {reading ? reading.humidity : NO_READING}
              {reading && <span className="text-sm text-muted-foreground ml-1">%</span>}
            </div>
          </div>
        </div>

        {/* 24-Hour AQI Trend */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">24-Hour AQI Trend</span>
          </div>
          <div className="h-[100px] bg-muted/30 rounded-lg">
            {reading ? (
              <Chart
                type="area"
                options={aqiChartOptions}
                series={[{ name: 'AQI', data: aqiData }]}
                height={100}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">Awaiting air-quality sensor</span>
              </div>
            )}
          </div>
        </div>

        {/* 24-Hour CO2 Trend */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Wind className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">24-Hour CO₂ Trend</span>
          </div>
          <div className="h-[100px] bg-muted/30 rounded-lg">
            {reading ? (
              <Chart
                type="line"
                options={co2ChartOptions}
                series={[{ name: 'CO2', data: co2Data }]}
                height={100}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">Awaiting air-quality sensor</span>
              </div>
            )}
          </div>
        </div>

        {/* AQI Scale Reference */}
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">AQI Scale Reference</p>
          <div className="flex gap-1">
            <div className="flex-1 h-2 rounded-l bg-success" title="Good (0-50)" />
            <div className="flex-1 h-2 bg-warning" title="Moderate (51-100)" />
            <div className="flex-1 h-2 bg-orange-500" title="Unhealthy for Sensitive (101-150)" />
            <div className="flex-1 h-2 bg-error" title="Unhealthy (151-200)" />
            <div className="flex-1 h-2 rounded-r bg-red-700" title="Very Unhealthy (201+)" />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>0</span>
            <span>50</span>
            <span>100</span>
            <span>150</span>
            <span>200+</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
